// LoadingProgressService.ts - Сервис для отслеживания прогресса загрузки
import { atom } from 'nanostores';
import { eventBus } from '@/engine/events/EventBus';
import { logger } from '@/engine/logging';

export interface LoadingStep {
  id: string;
  label: string;
  weight: number;
  completed: boolean;
}

export interface LoadingProgress {
  currentStep: string;
  progress: number;
  status: string;
  steps: LoadingStep[];
  isComplete: boolean;
}

export class LoadingProgressService {
  private static instance: LoadingProgressService;

  // Reactive stores
  public readonly progressStore = atom<LoadingProgress>({
    currentStep: 'core',
    progress: 0,
    status: 'Initializing...',
    steps: [
      { id: 'core', label: 'Initializing Core Systems', weight: 10, completed: false },
      { id: 'canvas', label: 'Setting up Canvas', weight: 5, completed: false },
      { id: 'game', label: 'Loading Game Engine', weight: 20, completed: false },
      { id: 'assets', label: 'Loading Assets', weight: 30, completed: false },
      { id: 'textures', label: 'Loading Textures', weight: 20, completed: false },
      { id: 'ui', label: 'Preparing Interface', weight: 10, completed: false },
      { id: 'complete', label: 'Ready to Launch!', weight: 5, completed: false },
    ],
    isComplete: false,
  });

  private constructor() {
    // Listen for internal events
    eventBus.on('loading-step-start', this.handleStepStart.bind(this));
    eventBus.on('loading-step-complete', this.handleStepComplete.bind(this));
    eventBus.on('loading-progress-update', this.handleProgressUpdate.bind(this));
  }

  public static getInstance(): LoadingProgressService {
    if (!this.instance) {
      this.instance = new LoadingProgressService();
    }
    return this.instance;
  }

  /**
   * Start a loading step
   */
  public startStep(stepId: string, customLabel?: string): void {
    const step = this.findStep(stepId);
    if (!step) {
      logger.warn(`Unknown loading step: ${stepId}`, { source: 'LoadingProgressService' });
      return;
    }

    const currentProgress = this.progressStore.get();

    this.progressStore.set({
      ...currentProgress,
      currentStep: stepId,
      status: customLabel || step.label,
    });

    // Emit event for UI updates
    eventBus.emit('loading-step', { step: stepId });

    logger.info(`🔄 Loading step started: ${stepId}`, { source: 'LoadingProgressService' });
  }

  /**
   * Complete a loading step
   */
  public completeStep(stepId: string): void {
    const step = this.findStep(stepId);
    if (!step) {
      logger.warn(`Unknown loading step: ${stepId}`, { source: 'LoadingProgressService' });
      return;
    }

    const currentProgress = this.progressStore.get();
    const updatedSteps = currentProgress.steps.map(s =>
      s.id === stepId ? { ...s, completed: true } : s
    );

    // Calculate new progress
    const completedWeight = updatedSteps
      .filter(s => s.completed)
      .reduce((sum, s) => sum + s.weight, 0);

    const newProgress = Math.min(completedWeight, 100);
    const isComplete = newProgress >= 100;

    // Debug: Log all steps status
    logger.info(`📊 Steps status after completing ${stepId}:`, {
      completed: updatedSteps.filter(s => s.completed).map(s => `${s.id}(${s.weight}%)`),
      remaining: updatedSteps.filter(s => !s.completed).map(s => `${s.id}(${s.weight}%)`),
      totalWeight: updatedSteps.reduce((sum, s) => sum + s.weight, 0),
      completedWeight,
      newProgress,
      source: 'LoadingProgressService',
    });

    this.progressStore.set({
      ...currentProgress,
      progress: newProgress,
      steps: updatedSteps,
      isComplete,
    });

    // Emit events
    eventBus.emit('loading-progress', {
      progress: newProgress,
      status: isComplete ? 'Complete!' : currentProgress.status,
    });

    if (isComplete) {
      eventBus.emit('loading-complete');
      logger.info('🎉 Loading complete!', { source: 'LoadingProgressService' });
    } else {
      logger.info(`✅ Loading step completed: ${stepId} (${newProgress}%)`, {
        source: 'LoadingProgressService',
      });
    }
  }

  /**
   * Update progress within current step
   */
  public updateProgress(progress: number, status?: string): void {
    const currentProgress = this.progressStore.get();

    this.progressStore.set({
      ...currentProgress,
      progress: Math.min(Math.max(progress, 0), 100),
      status: status || currentProgress.status,
    });

    eventBus.emit('loading-progress', {
      progress: this.progressStore.get().progress,
      status: status || currentProgress.status,
    });
  }

  /**
   * Reset loading progress
   */
  public reset(): void {
    this.progressStore.set({
      currentStep: 'core',
      progress: 0,
      status: 'Initializing...',
      steps: this.progressStore.get().steps.map(step => ({ ...step, completed: false })),
      isComplete: false,
    });

    logger.info('🔄 Loading progress reset', { source: 'LoadingProgressService' });
  }

  /**
   * Get current progress
   */
  public getProgress(): LoadingProgress {
    return this.progressStore.get();
  }

  /**
   * Subscribe to progress changes
   */
  public subscribe(callback: (progress: LoadingProgress) => void): () => void {
    return this.progressStore.subscribe(callback);
  }

  private findStep(stepId: string): LoadingStep | undefined {
    return this.progressStore.get().steps.find(step => step.id === stepId);
  }

  private handleStepStart(data: { stepId: string; label?: string }): void {
    this.startStep(data.stepId, data.label);
  }

  private handleStepComplete(data: { stepId: string }): void {
    this.completeStep(data.stepId);
  }

  private handleProgressUpdate(data: { progress: number; status?: string }): void {
    this.updateProgress(data.progress, data.status);
  }
}

// Export singleton instance
export const loadingProgressService = LoadingProgressService.getInstance();
